"use client";

import {
    ArrowDownUp, ArrowUpDown, BarChart2, ChartArea, ChevronLeft, ChevronRight, CodeXmlIcon, Eye, Folder,
    MousePointerClick, RefreshCcwIcon, TrendingUp, UsersIcon, VideoIcon
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import api from "../../api/axios";
import DateFilter from "../../components/dashboard/DateRangeFilter";
import { getUser } from "../../api/auth";

/* ================= Collapse Icon ================= */
const CollapseIcon = ({ isExpanded, onClick }) => (
    <button
        onClick={onClick}
        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 bg-gray-200 transition-all duration-200 cursor-pointer group"
    >
        <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform duration-200 group-hover:text-gray-700 ${isExpanded ? "rotate-180" : ""
                }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
    </button>
);

/* ================= Metric Bar ================= */
const MetricBar = ({ value, max = 150, color = "blue" }) => {
    const percentage = Math.min((value / max) * 100, 100);

    const colorClasses = {
        blue: "from-blue-500 to-blue-600",
        green: "from-green-500 to-green-600",
        purple: "from-purple-500 to-purple-600",
        orange: "from-orange-500 to-orange-600",
    };

    return (
        <div className="flex items-center gap-3 w-full">
            <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                <div
                    className={`h-full bg-gradient-to-r ${colorClasses[color]} rounded-full transition-all duration-700 ease-out`}
                    style={{ width: `${percentage}%` }}
                />
            </div>
            <span className="text-sm font-semibold text-gray-700 min-w-[40px] text-right">
                {value}
            </span>
        </div>
    );
};

/* ================= Stat Card ================= */
const StatCard = ({ title, value, trend, color, icon }) => {
    const bgColor = color.replace("text-", "bg-").replace("-600", "-50");

    return (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300">
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl ${bgColor}`}>
                    <span className="text-2xl">{icon}</span>
                </div>
                {/* {trend !== undefined && (
                    <span
                        className={`text-sm px-3 py-1 rounded-full font-medium ${trend > 0 ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                            }`}
                    >
                        {trend > 0 ? "↗" : "↘"} {Math.abs(trend)}%
                    </span>
                )} */}
            </div>
            <p className="text-gray-600 text-sm font-medium mb-2">{title}</p>
            <p className={`text-3xl font-bold ${color}`}>
                {value.toLocaleString()}
            </p>
        </div>
    );
};

/* ================= Date Filter ================= */
const DateFilterComponent = ({ filter, onFilterChange, domainOptions = [], }) => (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-8">
        <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site
                    </label>
                    <select value={filter.site} onChange={(e) => onFilterChange({ ...filter, site: e.target.value })} className="w-full text-gray-900 px-4 py-3 border border-gray-200 rounded-xl">
                        <option value="">All Sites</option>
                        {domainOptions.map((item, index) => (
                            <option key={index} value={item} >
                                {item}
                            </option>
                        ))}
                    </select>
                </div> */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date
                    </label>
                    <DateFilter className="w-full" value={filter.startDate} onDateChange={(date) => onFilterChange({ ...filter, startDate: date })} />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                    </label>

                    <DateFilter className="w-full"
                        value={filter.endDate}
                        onDateChange={(date) => onFilterChange({ ...filter, endDate: date })} />
                </div>

            </div>
            <div className="flex items-end gap-3">
                <button
                    onClick={() => onFilterChange({ site: "", startDate: "", endDate: "" })}
                    className="px-6 py-3 text-sm font-medium border border-gray-300 rounded-xl"
                >
                    Clear
                </button>
            </div>
        </div>
    </div >
);

/* ================= Expanded Details ================= */
const ExpandedDetails = ({ data }) => {
    const trackingEventsProgress = ["start", "firstQuartile", "midpoint", "thirdQuartile", "complete"];
    const trackingEventsCards = ["error", "impression", "mute", "unmute", "pause", "resume", "click"];

    // convert stats array into object
    const statsMap = (data.stats || []).reduce((acc, item) => {
        const eventName = item.url.split("/").pop().split(".")[0];

        acc[eventName] = item.success_count;

        return acc;
    }, {});

    // progress metrics
    const progressMetrics = trackingEventsProgress.map((event) => ({
        label: progressLabelMap[event]?.label || event,
        value: statsMap[event] || 0,
        color: progressLabelMap[event]?.color || "blue",
    }));

    // engagement metrics
    const engagementMetrics = trackingEventsCards.map((event) => ({
        label: engagementLabelMap[event]?.label || event,
        value: statsMap[event] || 0,
        bg: engagementLabelMap[event]?.bg || "bg-gray-50",
        text: engagementLabelMap[event]?.text || "text-gray-700",
        border: engagementLabelMap[event]?.border || "border-gray-100",
    }));

    return (
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 border border-gray-200 rounded-2xl p-6 mt-4 mx-4 mb-4">
            <div className="flex items-center justify-between mb-6">
                <h4 className="font-semibold text-gray-800 text-md flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                        <span className="text-gray-600"><ChartArea className="h-5 w-5" /></span>
                    </div>
                    Performance Details for {data?.adv_name}
                </h4>
                <div className="text-sm text-gray-500 font-bold">
                    {new Date(data?.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                {/* Progress Metrics */}
                <div className="space-y-6">
                    <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                        <span>
                            <BarChart2 className="h-5 w-5" />
                        </span>
                        Video Progress
                    </h5>
                    <div className="space-y-4">
                        {
                            // [
                            //   { label: "Start", value: data.start, color: "blue" },
                            //   { label: "First Quartile", value: data.firstQuartile, color: "blue" },
                            //   { label: "Midpoint", value: data.midpoint, color: "purple" },
                            //   { label: "Third Quartile", value: data.thirdQuartile, color: "purple" },
                            //   { label: "Complete", value: data.complete, color: "green" }
                            // ]
                            progressMetrics.map((metric, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100">
                                    <span className="text-sm font-medium text-gray-600 min-w-[120px]">{metric.label}</span>
                                    <div className="flex-1 max-w-[200px]">
                                        <MetricBar value={metric.value} color={metric.color} />
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {/* Engagement Metrics */}
                <div className="space-y-6">
                    <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                        <span>
                            <UsersIcon className="h-5 w-5" />
                        </span>
                        User Engagement
                    </h5>
                    <div className="grid grid-cols-2 gap-4">
                        {
                            // [
                            //   { label: "Mutes", value: data.mute, bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-100" },
                            //   { label: "Unmutes", value: data.unmute, bg: "bg-green-50", text: "text-green-700", border: "border-green-100" },
                            //   { label: "Pauses", value: data.pause, bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-100" },
                            //   { label: "Resumes", value: data.resume, bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-100" },
                            // ]
                            engagementMetrics.map((metric, index) => {
                                const isLastOddItem =
                                    engagementMetrics.length % 2 !== 0 &&
                                    index === engagementMetrics.length - 1;
                                return (
                                    <div key={index}
                                        className={`p-4 rounded-xl border-2 ${metric.bg} ${metric.border} text-center transition-all duration-300 hover:scale-105 ${isLastOddItem ? "col-span-2" : ""
                                            }`}
                                    // className={`p-4 rounded-xl border-2 ${metric.bg} ${metric.border} text-center transition-all duration-300 hover:scale-105`}
                                    >
                                        <div className={`text-2xl font-bold ${metric.text} mb-1`}>{metric.value}</div>
                                        <div className={`text-xs font-medium ${metric.text} opacity-80`}>{metric.label}</div>
                                    </div>
                                )
                            })}
                    </div>
                    {/* <div className={`p-4 rounded-xl border-2 bg-red-50 border-red-100 text-center transition-all duration-300 hover:scale-105`}>
            <div className="text-2xl font-bold text-red-700 mb-1">{data.clickthrough}</div>
            <div className="text-xs font-medium text-red-700 opacity-80">Click-throughs</div>
          </div> */}
                </div>

                {/* Resources */}
                <div className="space-y-6">
                    <h5 className="font-semibold text-gray-700 text-sm uppercase tracking-wide flex items-center gap-2">
                        <span>
                            <Folder className="h-5 w-5" />
                        </span>
                        Resources
                    </h5>
                    <div className="space-y-4">
                        {/* <a
              href={data.creativeUrl}
              target="_blank"
              rel="noreferrer"
              className="block p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-md transition-all duration-300 group"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                  <span className="text-xl">
                    <VideoIcon className="h-5 w-5" />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-sm mb-1">Creative Asset</div>
                  <div className="text-xs text-gray-500 truncate">{data.creativeUrl}</div>
                </div>
                <div className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  ↗
                </div>
              </div>
            </a> */}
                        <a
                            href={data.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block p-4 bg-white rounded-xl border-2 border-gray-200 hover:border-green-300 hover:shadow-md transition-all duration-300 group"
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-green-100 rounded-lg group-hover:scale-110 transition-transform duration-300">
                                    <span className="text-xl">
                                        <CodeXmlIcon className="h-5 w-5" />
                                    </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-gray-900 text-sm mb-1">VAST XML</div>
                                    <div className="text-xs text-gray-500 truncate">{data.url}</div>
                                </div>
                                <div className="text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    ↗
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    )
};

/* ================= Main Dashboard ================= */
export default function DashboardComponent({ role }) {

    const [dateFilter, setDateFilter] = useState({
        site: "",
        startDate: "",
        endDate: "",
    });

    const [expandedRows, setExpandedRows] = useState({});

    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);

    const [metrics, setMetrics] = useState({});
    const [metricsLoading, setMetricsLoading] = useState(false);

    const [domainOptions, setDomainOptions] = useState([]);

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 5,
        total: 0
    });

    const userDetail = getUser();
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
      //  fetchDomains();
    }, []);

    useEffect(() => {
        fetchReports();
    }, [pagination.page, pagination.limit, dateFilter]);

    useEffect(() => {
        fetchMetrics();
    }, [dateFilter]);


    const fetchDomains = async () => {
        try {
            const response = await api.get("/lookup/domains");
            setDomainOptions(response.data?.data || []);
        } catch (error) {
            console.error("Error fetching domains", error);
        }
    };

    const fetchMetrics = async () => {
        try {
            setMetricsLoading(true);

            const payload = {
                start_date: dateFilter.startDate || "",
                end_date: dateFilter.endDate || "",
                // advertisers: dateFilter.site ? [dateFilter.site] : [],
            };

            const response = await api.post("/dashboard/anaylytics/metrics", payload);
            setMetrics(response?.data?.metrics || {});
        } catch (error) {
            console.error("Error fetching metrics", error);
        } finally {
            setMetricsLoading(false);
        }
    };

    const fetchReports = async () => {
        try {
            setLoading(true);

            const payload = {
                page: pagination.page,
                limit: pagination.limit,
                start_date: dateFilter.startDate,
                end_date: dateFilter.endDate,
                // advertisers: dateFilter.site ? [dateFilter.site] : [],
            }

            const response = await api.post("/dashboard/anaylytics", payload);

            const data = response?.data?.data
            setReports(data?.list);
            setPagination((prev) => ({
                ...prev,
                total: data?.count,
            }));
        } catch (error) {
            console.error("Error fetching reports", error);
        } finally {
            setLoading(false);
        }
    };

    const totalPages = Math.ceil(pagination.total / pagination.limit);

    const filteredData = useMemo(() => {
        if (!dateFilter.startDate && !dateFilter.endDate) return reports;

        return reports.filter((item) => {
            const itemDate = new Date(item.dateTime);
            const start = dateFilter.startDate
                ? new Date(dateFilter.startDate)
                : null;
            const end = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

            if (start && itemDate < start) return false;
            if (end && itemDate > end) return false;
            return true;
        });
    }, [reports, dateFilter]);

    // const summary = useMemo(() => {
    //   const totalStarts = filteredData.reduce((sum, row) => sum + row.start, 0);
    //   const totalCompletions = filteredData.reduce((sum, row) => sum + row.complete, 0);
    //   const totalClicks = filteredData.reduce((sum, row) => sum + row.clickthrough, 0);
    //   const totalXmlCount = filteredData.reduce((sum, row) => sum + row.xmlCount, 0);
    //   const completionRate = totalStarts > 0 ? (totalCompletions / totalStarts * 100) : 0;

    //   return {
    //     totalStarts,
    //     totalCompletions,
    //     totalClicks,
    //     totalXmlCount,
    //     completionRate
    //   };
    // }, [filteredData]);

    const summary = useMemo(() => {
        return {
            totalStarts: metrics?.total_impressions || 0,
            totalClicks: metrics?.click_throughs || 0,
            totalXmlCount: metrics?.total_xml_hits || 0,
            completionRate: metrics?.completion_rate || 0,
        };
    }, [metrics]);


    const toggleRow = (id) => {
        setExpandedRows((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const getRowId = (item) =>
        `${item.date}-${item.adv_name}-${item.url}`;

    if (!userDetail) {
        return (
            <div className="h-[90vh] flex items-center justify-center px-6">
                <div className="max-w-lg w-full bg-white border border-gray-100 shadow-xl rounded-3xl p-10 text-center">

                    {/* Icon */}
                    <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg mb-6">
                        <ChartArea className="w-10 h-10 text-white" />
                    </div>

                    {/* Heading */}
                    <h2 className="text-3xl font-bold text-gray-900 mb-3">
                        Access Dashboard Metrics
                    </h2>

                    {/* Description */}
                    <p className="text-gray-500 text-base leading-7 mb-8">
                        Please register or login to the system to view
                        performance analytics, tracking reports, impressions,
                        clicks, and detailed ad metrics.
                    </p>

                    {/* Buttons */}
                    <div className="flex items-center justify-center gap-4">
                        <button
                            onClick={() => navigate("/")}
                            className="px-6 py-3 rounded-xl bg-black text-white font-medium hover:opacity-90 transition-all"
                        >
                            Login
                        </button>

                        <button
                            onClick={() => navigate("/auth/sign-up")}
                            className="px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all"
                        >
                            Register
                        </button>
                    </div>

                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br -from-slate-50 to-blue-50/30">
            {/* Header */}
            <div className="mb-2">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6">
                    <div className="mb-1 lg:mb-0">
                        <h1 className="premium-page-title">Ad Performance Dashboard</h1>
                    </div>
                </div>

                {/* Date Filter */}
                <DateFilterComponent
                    filter={dateFilter}
                    onFilterChange={setDateFilter}
                    domainOptions={domainOptions}
                />

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                    {metricsLoading ? (
                        [...Array(4)].map((_, index) => (
                            <StatCardSkeleton key={index} />
                        ))
                    ) : (
                        <>
                            <StatCard
                                title="Total XML Hits"
                                value={summary.totalXmlCount}
                                trend={15}
                                color="text-blue-600"
                                icon={<CodeXmlIcon className="w-6 h-6 text-blue-600" />}
                            />

                            <StatCard
                                title="Total Impressions"
                                value={summary.totalStarts}
                                trend={12}
                                color="text-green-600"
                                icon={<Eye className="w-6 h-6 text-blue-600" />}
                            />

                            <StatCard
                                title="Click-throughs"
                                value={summary.totalClicks}
                                trend={-3}
                                color="text-purple-600"
                                icon={<MousePointerClick className="w-6 h-6 text-purple-600" />}
                            />

                            <StatCard
                                title="Completion Rate"
                                value={`${Math.round(parseFloat(summary.completionRate))}%`}
                                trend={5}
                                color="text-orange-600"
                                icon={<TrendingUp className="w-6 h-6 text-orange-600" />}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Main Data Table */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">Tracking Report</h2>
                            <p className="text-gray-600 mt-2">Click on any row to view detailed performance metrics</p>
                        </div>
                        <div className="mt-4 lg:mt-0 flex items-center gap-4">
                            <span className="px-4 py-2 bg-blue-100 text-black rounded-lg text-sm font-medium">
                                {filteredData?.length} records
                            </span>
                            <span className="px-2 py-2 bg-blue-100 text-black rounded-lg text-sm font-medium cursor-pointer"
                                onClick={() => { fetchReports(); }}>
                                <RefreshCcwIcon />
                            </span>
                        </div>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50/80 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">
                                    {/* Collapse icon column */}
                                </th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                                    <div className="flex gap-1">
                                        Date & Time
                                    </div>
                                </th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                                    <div className="flex gap-1">
                                        Advertiser
                                    </div>
                                </th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                                    <div className="flex gap-1">
                                        Creative Name
                                    </div>
                                </th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                                    <div className="flex gap-1">
                                        XML URL
                                    </div>
                                </th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                                    <div className="flex gap-1">
                                        Requests
                                    </div>
                                </th>
                                <th className="px-6 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-wider cursor-pointer">
                                    <div className="flex gap-1">
                                        Success
                                    </div>
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-200/60">
                            {loading ? <TableRowSkeleton /> :
                                filteredData?.length > 0 ?
                                    <>
                                        {filteredData.map((item) => {
                                            const rowId = getRowId(item);
                                            const isExpanded = expandedRows[rowId];

                                            return (
                                                <React.Fragment key={rowId}>
                                                    <tr
                                                        key={rowId}
                                                        className={`hover:bg-gray-50/80  transition-all duration-200 ${isExpanded ? 'bg-blue-50/50' : ''
                                                            }`}
                                                    >
                                                        {/* Collapse Icon */}
                                                        <td className="px-6 py-5 ">
                                                            <CollapseIcon
                                                                isExpanded={isExpanded}
                                                                onClick={() => toggleRow(rowId)}
                                                            />
                                                        </td>

                                                        {/* Date */}
                                                        <td className="px-6 py-5 w-80">
                                                            <div className="text-gray-900 font-semibold">
                                                                {new Date(item.date).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric'
                                                                })}
                                                            </div>
                                                        </td>

                                                        {/* Site */}
                                                        <td className="px-6 py-5">
                                                            <div className="flex items-center gap-3">
                                                                {/* <div className={`w-4 h-4 rounded-full ${item.adv_name === "example.com" ? "bg-gradient-to-r from-green-400 to-green-500" :
                            item.adv_name === "news.site" ? "bg-gradient-to-r from-blue-400 to-blue-500" :
                              item.adv_name === "blog.example.com" ? "bg-gradient-to-r from-purple-400 to-purple-500" :
                                item.adv_name === "shop.demo" ? "bg-gradient-to-r from-orange-400 to-orange-500" : "bg-gray-400"
                            }`}></div> */}
                                                                <span className="font-semibold text-gray-900">{item.adv_name || '-'}</span>
                                                            </div>
                                                        </td>

                                                        <td className="px-6 py-5 w-80">
                                                            <div className="flex items-center gap-3">
                                                                <span className="font-semibold text-gray-900">{item.creative_name || '-'}</span>
                                                            </div>
                                                        </td>

                                                        {/* XML URL */}
                                                        <td className="px-6 py-5">
                                                            <a
                                                                href={item.url}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                className="text-blue-600 hover:text-blue-700 hover:underline font-medium text-sm truncate max-w-[240px] block transition-all duration-200"
                                                                title={item.url}
                                                            >
                                                                {item.url.replace('https://', '')}
                                                            </a>
                                                        </td>

                                                        {/* Hit Count */}
                                                        <td className="px-6 py-5">
                                                            <div className="flex items-center justify-start">
                                                                <span className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white font-bold text-sm shadow-sm">
                                                                    {item.request_count.toLocaleString()}
                                                                </span>
                                                            </div>
                                                        </td>

                                                        {/* Impression Count */}
                                                        <td className="px-6 py-5">
                                                            <div className="flex items-center justify-start">
                                                                <span className="inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-sm shadow-sm">
                                                                    {item.success_count.toLocaleString()}
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>

                                                    {/* Expanded Details Row */}
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={7} className="p-0">
                                                                <ExpandedDetails data={item} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </> :
                                    <tr>
                                        <td
                                            colSpan={7}
                                            className="text-center py-28 text-xl text-gray-600"
                                        >
                                            No records found
                                        </td>
                                    </tr>
                            }
                        </tbody>
                    </table>
                </div>

                {/* Table Footer */}
                <div className="px-6 py-6 border-t border-gray-200 bg-gray-50/50">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-4">
                            <span>Showing {filteredData?.length} of {reports?.length} records</span>
                            {dateFilter.startDate && (
                                <span className="px-3 py-1 bg-white rounded-lg border border-gray-200">
                                    Filtered: {dateFilter.startDate} to {dateFilter.endDate || 'present'}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <button className="flex items-center px-4 py-2 rounded-lg hover:bg-white transition-all duration-200 font-medium"
                                disabled={pagination.page === 1}
                                onClick={() =>
                                    setPagination((prev) => ({
                                        ...prev,
                                        page: prev.page - 1,
                                    }))
                                }>
                                <ChevronLeft size={18} /> Previous
                            </button>
                            <span className="px-4 py-2 bg-white rounded-lg border border-gray-200 font-medium">
                                Page {pagination.page} of {totalPages}
                            </span>
                            <button className="flex items-center px-4 py-2 rounded-lg hover:bg-white transition-all duration-200 font-medium"
                                disabled={pagination.page === totalPages}
                                onClick={() =>
                                    setPagination((prev) => ({
                                        ...prev,
                                        page: prev.page + 1,
                                    }))
                                }>
                                Next  <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center">
                <p className="text-sm text-gray-500">
                    Data updates in real-time • Last updated: {new Date().toLocaleTimeString()}
                </p>
            </div>
        </div>
    );
}

const progressLabelMap = {
    start: {
        label: "Start",
        color: "blue",
    },

    firstQuartile: {
        label: "First Quartile",
        color: "blue",
    },

    midpoint: {
        label: "Midpoint",
        color: "purple",
    },

    thirdQuartile: {
        label: "Third Quartile",
        color: "purple",
    },

    complete: {
        label: "Complete",
        color: "green",
    },
};

const engagementLabelMap = {
    click: {
        label: "Clicks",
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-100",
    },

    error: {
        label: "Errors",
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-200",
    },

    impression: {
        label: "Impressions",
        bg: "bg-indigo-50",
        text: "text-indigo-700",
        border: "border-indigo-100",
    },

    mute: {
        label: "Mutes",
        bg: "bg-purple-50",
        text: "text-purple-700",
        border: "border-purple-100",
    },

    unmute: {
        label: "Unmutes",
        bg: "bg-green-50",
        text: "text-green-700",
        border: "border-green-100",
    },

    pause: {
        label: "Pauses",
        bg: "bg-orange-50",
        text: "text-orange-700",
        border: "border-orange-100",
    },

    resume: {
        label: "Resumes",
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-100",
    },
};

const StatCardSkeleton = () => {
    return (
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 animate-pulse">
            <div className="flex items-center justify-between mb-4">
                <div className="w-14 h-14 rounded-xl bg-gray-100" />
                <div className="w-16 h-6 rounded-full bg-gray-100" />
            </div>

            <div className="w-1/2 h-4 bg-gray-100 rounded mb-3" />
            <div className="w-2/3 h-8 bg-gray-100 rounded" />
        </div>
    );
};

const TableRowSkeleton = () => {
    return (
        <>
            {[...Array(5)].map((_, index) => (
                <tr key={index} className="animate-pulse border-b border-gray-100">
                    <td className="px-6 py-5">
                        <div className="w-8 h-8 rounded-lg bg-gray-200" />
                    </td>

                    <td className="px-6 py-5">
                        <div className="w-28 h-5 bg-gray-200 rounded" />
                    </td>

                    <td className="px-6 py-5">
                        <div className="w-40 h-5 bg-gray-200 rounded" />
                    </td>

                    <td className="px-6 py-5">
                        <div className="w-36 h-5 bg-gray-200 rounded" />
                    </td>

                    <td className="px-6 py-5">
                        <div className="w-52 h-5 bg-gray-200 rounded" />
                    </td>

                    <td className="px-6 py-5">
                        <div className="w-16 h-10 rounded-xl bg-gray-200" />
                    </td>

                    <td className="px-6 py-5">
                        <div className="w-16 h-10 rounded-xl bg-gray-200" />
                    </td>
                </tr>
            ))}
        </>
    );
};